import $ from 'jquery';
import React, { Fragment, useState, useEffect } from 'react';
import Tippy from '@tippyjs/react';
import { createPortal } from 'react-dom';
import Select from 'react-select';
import arrayUtility from 'lodash/array';
import { CATEGORY_VISIBILITY, CATEGORY_ORDER_BY_DEFAULT, USER_ROLE } from '../../utilities/CONSTANTS';
import {
    getCategoriesUrl,
    getApiUsersAndGroupsUrl,
    getUpgradePlanUrl,
    getUserGroupsUrl, getFolderUrl, getExternalSitesUrl,
} from '../../utilities/urlHelper';

import ColorPicker from '../CategoryColorPicker';
import SlackConversationSelector from '../SlackConversationSelector';

import { hasPrivateCategories, hasPublicCategories, hasSlackFeature } from '../../utilities/featureHelper';
import useCategories from '../../hooks/useCategories';
import TettraEditor from '../../tettra-editor/components/TettraEditor';
import { categoryToEditorState } from '../../tettra-editor/utilities/contentToEditorState';
import editorStateToRawContent from '../../tettra-editor/utilities/editorStateToRawContent';
import editorStateToHTML from '../../tettra-editor/utilities/editorStateToHTML';
import { randomHex } from '../../common/utils';

const categoryIcons = CATEGORY_ICONS;
const CATEGORY_BASE_PATH = '/img/category-icons/';
const DEFAULT_ICON = 'category.svg';
const EDITOR_CONFIG = {
    enableSmallEditor: true,
    spellCheck: true,
    enableAutosave: false,
    enableTables: false,
    enableInsertControls: false,
    enableMentionSuggestions: true,
    enableInternalSuggestions: true,
    enableExternalSuggestions: false,
    enableFileUpload: false,
};

const NotificationCheckbox = ({ label, isChecked, onChange }) => (
    <div className="mb2">
        <label>
            <input
                type="checkbox"
                checked={isChecked}
                onChange={onChange}
            />
            {label}
        </label>
    </div>
);

const CategoryOrder = ({ defaultOrder, isSubCategory }) => (
    <div className="mb3">
        <label className="bold block col-12 mb1">
            What order should the items appear in?
        </label>
        <select defaultValue={defaultOrder} className="'block col-6 field" name="order_type">
            { Object.keys(CATEGORY_ORDER_BY_DEFAULT).map((key) => {
                const name = key.toLowerCase().split('_').join(' ');
                return (
                    <option value={CATEGORY_ORDER_BY_DEFAULT[key]} key={key} title={name}>
                        {name}
                    </option>
                );
            })}
        </select>
        {isSubCategory
            && (
                <p className="yellow italic mb0 mt1">
                Changing to a custom order will unpin any pinned items in this subcategory.
                </p>
            )}
    </div>
);

const CategoryIcons = ({ color, defaultIcon }) => (
    <div className="mb3">
        <label className="bold blocl col-12 mb1">
            Icon
        </label>
        <div className="clearfix">
            {categoryIcons.map((icon) => (
                <label className="img-radio" key={icon}>
                    <input type="radio" value={CATEGORY_BASE_PATH + icon} name="icon" defaultChecked={defaultIcon ? (CATEGORY_BASE_PATH + icon === defaultIcon) : (icon === DEFAULT_ICON)} />
                    <img height="30" width="30" src={CATEGORY_BASE_PATH + icon} style={{ backgroundColor: `#${color}` }} />
                </label>
            ))}
        </div>
    </div>

);

const orderOptions = options => (
    options
        && options.filter(v => v.isFixed).concat(options.filter(v => !v.isFixed))
);

const styles = {
    multiValue(base, state) {
        return state.data.isFixed ? { ...base, backgroundColor: 'gray' } : base;
    },
    multiValueLabel(base, state) {
        return state.data.isFixed
            ? {
                ...base, fontWeight: 'bold', color: 'white', paddingRight: 6,
            } : base;
    },
    multiValueRemove(base, state) {
        return state.data.isFixed ? { ...base, display: 'none' } : base;
    },
};

const setOptionAsFixed = (options, ownerIds) => {
    options.length && options.forEach(option => {
        option.isFixed = ((ownerIds && ownerIds.includes(option.value)) || option.value === window.userId);
    });

    return options;
};

const CategoryVisibilityGroupsSelect = ({ usersAndGroups, defaultValue, ownerIds }) => {
    if (!usersAndGroups) {
        return <div className="loading gray ml3 mb2">Loading users & groups conversations</div>;
    }

    const [optionsList, setOptionsList] = useState(defaultValue.length ? defaultValue : usersAndGroups.filter(({ value }) => value === window.userId));

    useEffect(() => {
        let optionsListCopy = optionsList;
        usersAndGroups = setOptionAsFixed(usersAndGroups, ownerIds);
        optionsListCopy = setOptionAsFixed(optionsListCopy, ownerIds);
        defaultValue = setOptionAsFixed(defaultValue, ownerIds);

        const newlySelected = usersAndGroups.filter(({ value }) => (ownerIds && ownerIds.includes(value)));
        const uniqueOptionsList = arrayUtility.uniqBy(optionsListCopy.concat(newlySelected), 'value');

        setOptionsList(orderOptions(uniqueOptionsList));
    }, [ownerIds]);

    const handleChange = (selectedUsers, { removedValue }) => {
        if (removedValue && removedValue.isFixed) {
            return;
        }

        setOptionsList(orderOptions(selectedUsers));
    };

    return (
        <Select
            name="usersAndGroups[]"
            isMulti
            styles={styles}
            isClearable={false}
            placeholder="Choose users & groups"
            options={usersAndGroups}
            value={optionsList}
            className="mb2"
            onChange={handleChange}
        />
    );
};

const CategoryOwnerSelect = ({ users, ownerIds, setOwnerIds }) => {
    const currentUser = ownerIds
        ? users && users.filter((u) => ownerIds.includes(u.value))
        : users && users.find(({ value }) => value == window.userId);

    if (!users) {
        return <div className="loading gray ml3 mb2">Loading users</div>;
    }

    const handleChange = selectedValues => {
        selectedValues ? setOwnerIds(selectedValues.map((v) => v.value)) : setOwnerIds([]);
    };

    return (
        <Select
            name="owners[]"
            isMulti
            options={users}
            defaultValue={currentUser}
            onChange={handleChange}
        />
    );
};

const CategoryVisibility = ({
    category, usersAndGroups, closeModal, defaultVisibility, authorizedUsers, ownerIds,
}) => {
    const [selectedVisibility, setSelectedVisibility] = useState(defaultVisibility ?? CATEGORY_VISIBILITY.TEAM_VISIBLE);
    const showPublicCategoriesOption = hasPublicCategories() && !!window.adminAllowsPublicCategories && !!window.publicSharingEnabled;
    const showContactUsForPublicCategories = hasPublicCategories() && !window.publicSharingEnabled;

    return (
        <div className="mb3">
            <label htmlFor="usersAndGroups[]" className="bold block col-12 mb1">Access level</label>
            <p>
                <label>
                    <input
                        name="visibility"
                        type="radio"
                        value={CATEGORY_VISIBILITY.TEAM_VISIBLE}
                        onChange={() => {
                            setSelectedVisibility(CATEGORY_VISIBILITY.TEAM_VISIBLE);
                        }}
                        checked={selectedVisibility === CATEGORY_VISIBILITY.TEAM_VISIBLE}
                    />
                    <img src="/img/icons/icon-visibility-team.png" alt="visibility-team" className="mr1 left" height="30" width="30" />
                    Everyone at
                    {' '}
                    {window.teamName}
                </label>
            </p>
            { !hasPrivateCategories()
                && (
                    <p>
                        <label className="gray">
                            <input type="radio" disabled />
                            <img src="/img/icons/icon-visibility-private.png" alt="visibility-private" className="mr1 left" height="30" width="30" />
                        Only invited people and groups
                            <Tippy content="Upgrade to enable private categories" placement="bottom">
                                <a
                                    href={getUpgradePlanUrl()}
                                    className="btn-tertiary ml1 btn-teal upgrade-button"
                                >
                                    Upgrade
                                </a>
                            </Tippy>
                        </label>
                    </p>
                )}
            { hasPrivateCategories()
                && (
                    <p>
                        <label>
                            <input
                                name="visibility"
                                type="radio"
                                value={CATEGORY_VISIBILITY.PRIVATE_VISIBLE}
                                onChange={() => {
                                    setSelectedVisibility(CATEGORY_VISIBILITY.PRIVATE_VISIBLE);
                                }}
                                checked={selectedVisibility === CATEGORY_VISIBILITY.PRIVATE_VISIBLE}
                            />
                            <img src="/img/icons/icon-visibility-private.png" alt="visibility-private" className="mr1 left" height="30" width="30" />
                        Only invited people and
                            {' '}
                            <a href={getUserGroupsUrl()} target="_blank" rel="noopener noreferrer">groups</a>
                        </label>
                    </p>
                )}
            { selectedVisibility === CATEGORY_VISIBILITY.PRIVATE_VISIBLE && <CategoryVisibilityGroupsSelect ownerIds={ownerIds} defaultValue={authorizedUsers} usersAndGroups={usersAndGroups} />}
            { showPublicCategoriesOption
                && (
                    <Fragment>
                        <p>
                            <label>
                                <input
                                    name="visibility"
                                    type="radio"
                                    value={CATEGORY_VISIBILITY.PUBLIC_VISIBLE}
                                    onChange={() => {
                                        setSelectedVisibility(CATEGORY_VISIBILITY.PUBLIC_VISIBLE);
                                    }}
                                    checked={selectedVisibility === CATEGORY_VISIBILITY.PUBLIC_VISIBLE}
                                />
                                <img src="/img/icons/icon-visibility-public.png" alt="visibility-team" className="mr1 left" height="30" width="30" />
                            The public
                            </label>
                        </p>
                        { selectedVisibility === CATEGORY_VISIBILITY.PUBLIC_VISIBLE && <p className="notification yellow"><em>This category and all its pages will be accessible on the public internet.</em></p> }
                    </Fragment>
                )}
            { showContactUsForPublicCategories
                && (
                    <Fragment>
                        <p>
                            <label className="gray">
                                <input
                                    name="visibility"
                                    type="radio"
                                    value={CATEGORY_VISIBILITY.PUBLIC_VISIBLE}
                                    disabled
                                />
                                <img src="/img/icons/icon-visibility-public.png" alt="visibility-team" className="mr1 left" height="30" width="30" />
                            The public
                            </label>
                        </p>
                        <p className="notification yellow">
                            <em>
                                Please
                                <a
                                    href="#"
                                    onClick={() => { closeModal(); loadAndBootIntercomIfUndefined(); Intercom('show'); }}
                                >
                                    {' contact us'}
                                </a>
                                {' to enable public categories for your team.'}
                            </em>
                        </p>
                    </Fragment>
                )}
            {category?.external_sites?.length > 0
                && (
                    <p className="warning-message mt-2">
                        <span>
                            This category is published as an
                            {' '}
                            <a href={getExternalSitesUrl()} target="_blank" rel="noreferrer">external site</a>
                        </span>
                    </p>
                )}
        </div>
    );
};

const CreateCategoryModal = (props) => {
    const { category_id, folder_id } = props;
    const categories = useCategories();
    const category = category_id ? (categories[category_id].folders[folder_id] ? categories[category_id].folders[folder_id] : categories[category_id]) : null;
    const categorySettings = category?.settings || {
        notify_new_questions: true,
        notify_answered_questions: true,
        notify_new_suggestions: true,
        notify_closed_suggestions: true,
        notify_new_comments: true,
        notify_new_pages: true,
        notify_page_updates: false,
    };

    const isSubCategory = category && category.category_id !== undefined;
    const [color, setColor] = useState(category ? category.color : randomHex());
    const [usersAndGroups, setUsersAndGroups] = useState();
    const [possibleOwners, setPossibleOwners] = useState();
    const [editorState, setEditorState] = useState(categoryToEditorState(category));
    const [isUploading, setUploading] = useState(null);
    const html = editorStateToHTML(editorState);
    const contentStateJSON = JSON.stringify(editorStateToRawContent(editorState));
    const [ownerIds, setOwnerIds] = useState(category ? category.owner_ids : [window.userId]);
    const [settings, setSettings] = useState({
        notifyNewQuestions: categorySettings.notify_new_questions ? 1 : 0,
        notifyAnsweredQuestions: categorySettings.notify_answered_questions ? 1 : 0,
        notifyNewSuggestions: categorySettings.notify_new_suggestions ? 1 : 0,
        notifyClosedSuggestions: categorySettings.notify_closed_suggestions ? 1 : 0,
        notifyNewComments: categorySettings.notify_new_comments ? 1 : 0,
        notifyNewPages: categorySettings.notify_new_pages ? 1 : 0,
        notifyPageUpdates: categorySettings.notify_page_updates ? 1 : 0,
    });

    const [hasSelectedConversations, setHasSelectedConversations] = useState(false);

    useEffect(() => {
        const url = getApiUsersAndGroupsUrl();
        $.get(url)
            .then(({ users, groups }) => {
                setUsersAndGroups(
                    users.map((user) => ({ value: user.id, label: user.display_name }))
                        .concat(groups.map((group) => ({ value: `G${group.id}`, label: `👥 ${group.name}` }))),
                );
                setPossibleOwners(users
                    .filter((user) => ([USER_ROLE.ADMIN, USER_ROLE.EDITOR].includes(user.role)))
                    .map((user) => ({ value: user.id, label: user.display_name })));
            });
    }, []);

    const onClickModal = ({ target }) => {
        const className = target.getAttribute('class');
        if (className && className.includes('react-modal open')) {
            return props.closeModal();
        }
    };

    const authorizedUsers = category && category.authorized_users || [];
    const authorizedGroups = category && category.authorized_groups || [];
    const authorizedUsersAndGroups = authorizedUsers.map((user) => ({ value: user.id, label: user.display_name }))
        .concat(authorizedGroups.map((group) => ({ value: `G${group.id}`, label: `👥 ${group.name}` })));

    const showSlack = window.hasSlack && hasSlackFeature();
    const formUrl = isSubCategory ? getFolderUrl(category.category_id, category.id) : (getCategoriesUrl() + (category ? `/${category.id}` : ''));
    return (
        <div className="react-modal open" onMouseDown={onClickModal}>
            <div className="modal-content p2 mb2">
                <h2>
                    {category ? 'Edit category' : 'Create new category'}
                </h2>
                <form method="POST" action={formUrl}>
                    {category && <input name="_method" type="hidden" value="PUT" />}
                    <input name="_token" type="hidden" value={window.csrfToken} />
                    <div className="form-group mb3">
                        <label htmlFor="name" className="bold block col-12 mb1">Name</label>
                        <input className="block col-12 mb2 field" placeholder="e.g. Announcements, Sales Playbook, etc." defaultValue={category && category.name} name="name" type="text" id="name" />
                    </div>
                    <div className="mb3">
                        <label htmlFor="description" className="bold block col-12 mb1">Description (optional)</label>
                        <div className="question-editor mb3">
                            <TettraEditor
                                config={EDITOR_CONFIG}
                                editorState={editorState}
                                updateModel={setEditorState}
                                setUploading={setUploading}
                            />
                        </div>
                        <input type="hidden" name="description" value={html} />
                        <input type="hidden" name="description_content_state" value={contentStateJSON} />
                    </div>
                    {!isSubCategory
                        && (
                            <div className="mb3">
                                <label htmlFor="owners" className="bold block col-12 mb1">Category owners</label>
                                <CategoryOwnerSelect setOwnerIds={setOwnerIds} ownerIds={ownerIds} users={possibleOwners} />
                            </div>
                        )}
                    <div className="mb3">
                        <ColorPicker pickColor={setColor} color={color} />
                    </div>
                    <CategoryIcons defaultIcon={category && category.icon} color={color} />
                    {!isSubCategory
                        && (
                            <CategoryVisibility
                                category={category}
                                ownerIds={ownerIds}
                                authorizedUsers={authorizedUsersAndGroups}
                                defaultVisibility={category && category.visibility}
                                usersAndGroups={usersAndGroups}
                                closeModal={props.closeModal}
                            />
                        )}
                    <CategoryOrder defaultOrder={category && category.order_type} isSubCategory={isSubCategory} />
                    { (showSlack && !isSubCategory)
                        && (
                            <div className="mb3">
                                <label className="bold block col-12 mb1">Slack notifications</label>
                                <div className="mb2">

                                    <SlackConversationSelector
                                        onConversationChange={(selectedConversations) => {
                                            setHasSelectedConversations(!!selectedConversations);
                                        }}
                                        initialConversationIds={category && category.slack_conversation && [category.slack_conversation.conversation_id]}
                                    />
                                </div>
                                <p className="m0 gray">
                                Don't see the private channel or group you're looking for? Add Tettra to that Slack conversation.
                                    <br />
                                    <a href="https://support.tettra.com/slack-integration/how-to-add-tettra-to-a-private-slack-channel" target="_blank" rel="noopener noreferrer">Instructions here.</a>
                                </p>
                                {hasSelectedConversations && (
                                    <div className="mb3 mt2">
                                        <label className="bold block col-12 mb2">Notification Settings</label>

                                        <NotificationCheckbox
                                            label="New Questions"
                                            isChecked={settings.notifyNewQuestions}
                                            onChange={() => setSettings({ ...settings, notifyNewQuestions: !settings.notifyNewQuestions })}
                                        />
                                        <NotificationCheckbox
                                            label="Answered Questions"
                                            isChecked={settings.notifyAnsweredQuestions}
                                            onChange={() => setSettings({ ...settings, notifyAnsweredQuestions: !settings.notifyAnsweredQuestions })}
                                        />
                                        <NotificationCheckbox
                                            label="New Suggestions"
                                            isChecked={settings.notifyNewSuggestions}
                                            onChange={() => setSettings({ ...settings, notifyNewSuggestions: !settings.notifyNewSuggestions })}
                                        />
                                        <NotificationCheckbox
                                            label="Closed Suggestions"
                                            isChecked={settings.notifyClosedSuggestions}
                                            onChange={() => setSettings({ ...settings, notifyClosedSuggestions: !settings.notifyClosedSuggestions })}
                                        />
                                        <NotificationCheckbox
                                            label="New Comments"
                                            isChecked={settings.notifyNewComments}
                                            onChange={() => setSettings({ ...settings, notifyNewComments: !settings.notifyNewComments })}
                                        />
                                        <NotificationCheckbox
                                            label="New Pages"
                                            isChecked={settings.notifyNewPages}
                                            onChange={() => setSettings({ ...settings, notifyNewPages: !settings.notifyNewPages })}
                                        />
                                        <NotificationCheckbox
                                            label="Page Updates"
                                            isChecked={settings.notifyPageUpdates}
                                            onChange={() => setSettings({ ...settings, notifyPageUpdates: !settings.notifyPageUpdates })}
                                        />
                                    </div>
                                )}

                            </div>

                        )}
                    <div className="right-align mt3">
                        <input type="hidden" name="settings[notifyNewQuestions]" value={settings.notifyNewQuestions ? 1 : 0} />
                        <input type="hidden" name="settings[notifyAnsweredQuestions]" value={settings.notifyAnsweredQuestions ? 1 : 0} />
                        <input type="hidden" name="settings[notifyNewSuggestions]" value={settings.notifyNewSuggestions ? 1 : 0} />
                        <input type="hidden" name="settings[notifyClosedSuggestions]" value={settings.notifyClosedSuggestions ? 1 : 0} />
                        <input type="hidden" name="settings[notifyNewComments]" value={settings.notifyNewComments ? 1 : 0} />
                        <input type="hidden" name="settings[notifyNewPages]" value={settings.notifyNewPages ? 1 : 0} />
                        <input type="hidden" name="settings[notifyPageUpdates]" value={settings.notifyPageUpdates ? 1 : 0} />

                        <span className="btn-secondary mr1" aria-label="Close" role="button" onClick={props.closeModal}>Cancel</span>
                        <input className="btn btn-primary" type="submit" value={(category ? 'Update ' : 'Create ') + (isSubCategory ? 'subcategory' : 'category')} />
                    </div>
                </form>
            </div>
        </div>
    );
};

const CreateOrEditCategoryWrapper = ({
    text, customClass, children, category_id, folder_id,
}) => {
    const [showModal, setShowModal] = useState(false);

    if (children) {
        return (
            <Fragment>
                <div className="contents" onClick={() => setShowModal(true)}>
                    {children}
                </div>
                {showModal && createPortal(<CreateCategoryModal folder_id={folder_id} category_id={category_id} closeModal={() => setShowModal(false)} />, document.body)}
            </Fragment>
        );
    }

    return (
        <Fragment>
            <span className={customClass} onClick={() => setShowModal(true)}>{text}</span>
            {showModal && createPortal(<CreateCategoryModal folder_id={folder_id} category_id={category_id} closeModal={() => setShowModal(false)} />, document.body)}
        </Fragment>
    );
};

export default CreateOrEditCategoryWrapper;
